var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { parseISO } from 'date-fns';
import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable } from 'mobx';
import nestedProperty from 'nested-property';
import React from 'react';
import { Button } from 'telia-front-react';
import { SingleConcurrentRequestController, safeReaction } from '../../common';
import HeaderJsxParser from '../../header/HeaderJsxParser';
import { isPreferencesCookieAllowed } from '../../utils';
const MILLISECONDS_IN_DAYS = 24 * 60 * 60 * 1000;
export default class OverlayNoticeStore {
    constructor(rootStore, initialNotice) {
        Object.defineProperty(this, "noticeLoadPromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "overlayNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "allCommonNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "commonNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "commonNoticeController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new SingleConcurrentRequestController()
        });
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "locationStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "setOverlayNotice", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (props) => {
                if (!props) {
                    if (!this.overlayNotices.length) {
                        return;
                    }
                    this.clearNotices();
                    return;
                }
                const isArray = Array.isArray(props);
                if (!isArray && (props === null || props === void 0 ? void 0 : props.isVisible) === false) {
                    this.clearNotices();
                }
                const newNotices = isArray ? props : [props];
                this.overlayNotices = this.getCommonNoticesAsNotices().slice().concat(newNotices);
            }
        });
        Object.defineProperty(this, "initCommonNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                this.noticeLoadPromise = this.fetchAllCommonNotices();
                this.allCommonNotices = await this.noticeLoadPromise;
                this.setCurrentOverlayNotices();
            }
        });
        Object.defineProperty(this, "fetchAllCommonNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                try {
                    const url = this.rootStore.environmentStore.URLData.getNotificationsURL('/notifications/common/all');
                    return await this.commonNoticeController.get(url);
                }
                catch (e) {
                    return [];
                }
            }
        });
        Object.defineProperty(this, "closeCommonNotice", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (notice, domId) => {
                const expires = Math.ceil((parseISO(notice.validTo).getTime() - new Date().getTime()) / MILLISECONDS_IN_DAYS);
                if (isPreferencesCookieAllowed()) {
                    Cookies.set(this.getCommonNoticeCookieName(notice.id), 'true', { domain: '.telia.ee', expires });
                }
                this.commonNotices = this.commonNotices.filter((commonNotice) => notice.id !== commonNotice.id).slice();
                this.overlayNotices = this.overlayNotices.filter((commonNotice) => domId !== commonNotice.id).slice();
            }
        });
        Object.defineProperty(this, "setCurrentOverlayNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.commonNotices = this.filterVisibleCommonNotices(this.allCommonNotices);
                this.addCommonNoticeTimeouts(this.commonNotices);
                this.overlayNotices = this.overlayNotices.concat(this.getCommonNoticesAsNotices());
            }
        });
        Object.defineProperty(this, "clearNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.overlayNotices = this.getCommonNoticesAsNotices();
                return;
            }
        });
        Object.defineProperty(this, "getCommonNoticeDOMId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => `commonNotice-${id}`
        });
        Object.defineProperty(this, "getCommonNoticeCookieName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => `commonNoticeClosed-${id}`
        });
        Object.defineProperty(this, "filterVisibleCommonNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (notices) => {
                if (!(notices === null || notices === void 0 ? void 0 : notices.length)) {
                    return [];
                }
                let currentNotices = this.filterCurrentNotices(notices);
                currentNotices = this.filterCommonNoticesByPath(currentNotices);
                return this.filterClosedNotices(currentNotices);
            }
        });
        Object.defineProperty(this, "filterCurrentNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (notices) => {
                const now = new Date();
                const isBeforeNow = (isoDate) => parseISO(isoDate).getTime() < now.getTime();
                return notices.filter((notice) => isBeforeNow(notice.validFrom) && !isBeforeNow(notice.validTo));
            }
        });
        Object.defineProperty(this, "filterCommonNoticesByPath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (notices) => {
                return notices.filter((notice) => new RegExp(notice.pattern).test(document.location.href));
            }
        });
        Object.defineProperty(this, "filterClosedNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (notices) => {
                const isClosed = (notice) => !Cookies.get(this.getCommonNoticeCookieName(notice.id));
                return notices.filter((notice) => isClosed(notice));
            }
        });
        Object.defineProperty(this, "addCommonNoticeTimeouts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (notices) => notices.map((notice) => notice.timeout && setTimeout(() => this.closeCommonNotice(notice, this.getCommonNoticeDOMId(notice.id)), notice.timeout * 1000))
        });
        Object.defineProperty(this, "resolveCommonNoticeChildren", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (notice, domId) => {
                var _a, _b;
                const { lang } = this.rootStore.environmentStore;
                if (!((_b = (_a = notice.cta) === null || _a === void 0 ? void 0 : _a.url) === null || _b === void 0 ? void 0 : _b[lang])) {
                    return React.createElement(React.Fragment, null, notice.content[lang]);
                }
                return (React.createElement("div", { className: "grid grid--gutter-default grid--between-xs grid--middle-xs grid--no-wrap-sm" },
                    React.createElement("div", { className: "grid__col grid__col--xs grid__col--max-sm" },
                        React.createElement("p", null, notice.content[lang || 'et'])),
                    React.createElement("div", { className: "grid__col grid__col--xs grid__col--min-sm" },
                        React.createElement(Button, { url: notice.cta.url[lang], text: notice.cta.text[lang], type: notice.cta.buttonType, onClick: (event) => {
                                this.closeCommonNotice(notice, domId);
                                this.locationStore.setLocation(event);
                            }, variant: "secondary", iconRight: "#arrow-right" }))));
            }
        });
        Object.defineProperty(this, "getCommonNoticesAsNotices", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.commonNotices.map((notice) => {
                    const domId = this.getCommonNoticeDOMId(notice.id);
                    return {
                        id: domId,
                        icon: notice.icon,
                        type: notice.level === 'info' ? undefined : notice.level,
                        onCloseClick: () => this.closeCommonNotice(notice, domId),
                        children: this.resolveCommonNoticeChildren(notice, domId)
                    };
                });
            }
        });
        makeObservable(this);
        this.rootStore = rootStore;
        this.locationStore = rootStore.locationStore;
        if (!nestedProperty.get(window, 'telia.megamenu.setOverlayNotice')) {
            nestedProperty.set(window, 'telia.megamenu.setOverlayNotice', this.setOverlayNotice);
        }
        if (initialNotice) {
            this.setOverlayNotice(initialNotice);
        }
        if (!rootStore.propStateStore.isSsr) {
            this.initCommonNotices();
        }
        safeReaction(rootStore, () => this.locationStore.currentPath, () => this.setCurrentOverlayNotices());
    }
    get getParsedOverlayNotice() {
        return this.overlayNotices.map((notice) => {
            if ((notice === null || notice === void 0 ? void 0 : notice.children) && !React.isValidElement(notice.children)) {
                return { ...notice, ...{ children: React.createElement(HeaderJsxParser, { content: notice.children }) } };
            }
            return notice;
        });
    }
}
__decorate([
    observable
], OverlayNoticeStore.prototype, "overlayNotices", void 0);
__decorate([
    observable
], OverlayNoticeStore.prototype, "allCommonNotices", void 0);
__decorate([
    observable
], OverlayNoticeStore.prototype, "commonNotices", void 0);
__decorate([
    action
], OverlayNoticeStore.prototype, "setOverlayNotice", void 0);
__decorate([
    action
], OverlayNoticeStore.prototype, "initCommonNotices", void 0);
__decorate([
    action
], OverlayNoticeStore.prototype, "fetchAllCommonNotices", void 0);
__decorate([
    action
], OverlayNoticeStore.prototype, "closeCommonNotice", void 0);
__decorate([
    computed
], OverlayNoticeStore.prototype, "getParsedOverlayNotice", null);
__decorate([
    action
], OverlayNoticeStore.prototype, "clearNotices", void 0);
//# sourceMappingURL=OverlayNoticeStore.js.map